'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const AIConfiguration = () => {
  const [config, setConfig] = useState({
    model: 'claude-sonnet-4-5-20250929',
    maxTokens: 1024,
    temperature: 0.7,
    autoRecommendations: true,
    priceMonitoring: true,
    visaAnalysis: true,
  });

  const models = [
    { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5 (Recommended)' },
    { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (Fast)' },
    { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5 (Advanced)' },
  ];

  const handleSave = () => {
    alert('AI configuration saved successfully!');
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-heading font-black text-foreground">AI Configuration</h2>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-6 py-3 bg-primary text-white font-bold rounded-lg hover:bg-secondary transition-all duration-200"
        >
          <Icon name="CheckIcon" size={20} />
          <span>Save Configuration</span>
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {/* Model Settings */}
        <div className="bg-white rounded-xl p-6 border border-border">
          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <Icon name="CpuChipIcon" size={24} className="text-primary" />
            Model Settings
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-foreground mb-2">
                AI Model
              </label>
              <select
                value={config?.model}
                onChange={(e) => setConfig({ ...config, model: e?.target?.value })}
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {models?.map((model) => (
                  <option key={model?.value} value={model?.value}>
                    {model?.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-foreground mb-2">
                Max Tokens: {config?.maxTokens}
              </label>
              <input
                type="range"
                min="256"
                max="4096"
                step="256"
                value={config?.maxTokens}
                onChange={(e) => setConfig({ ...config, maxTokens: parseInt(e?.target?.value) })}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-foreground mb-2">
                Temperature: {config?.temperature}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={config?.temperature}
                onChange={(e) => setConfig({ ...config, temperature: parseFloat(e?.target?.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>Precise</span>
                <span>Creative</span>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Toggles */}
        <div className="bg-white rounded-xl p-6 border border-border">
          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <Icon name="AdjustmentsHorizontalIcon" size={24} className="text-primary" />
            AI Features
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-surface rounded-lg">
              <div>
                <div className="font-bold text-foreground">Auto Recommendations</div>
                <div className="text-sm text-muted-foreground">AI-powered travel suggestions</div>
              </div>
              <button
                onClick={() => setConfig({ ...config, autoRecommendations: !config?.autoRecommendations })}
                className={`w-12 h-6 rounded-full transition-all duration-200 ${
                  config?.autoRecommendations ? 'bg-success' : 'bg-muted'
                }`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow-md transition-all duration-200 ${
                    config?.autoRecommendations ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-surface rounded-lg">
              <div>
                <div className="font-bold text-foreground">Price Monitoring</div>
                <div className="text-sm text-muted-foreground">Automated price alerts</div>
              </div>
              <button
                onClick={() => setConfig({ ...config, priceMonitoring: !config?.priceMonitoring })}
                className={`w-12 h-6 rounded-full transition-all duration-200 ${
                  config?.priceMonitoring ? 'bg-success' : 'bg-muted'
                }`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow-md transition-all duration-200 ${
                    config?.priceMonitoring ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-surface rounded-lg">
              <div>
                <div className="font-bold text-foreground">Visa Analysis</div>
                <div className="text-sm text-muted-foreground">AI visa requirement checks</div>
              </div>
              <button
                onClick={() => setConfig({ ...config, visaAnalysis: !config?.visaAnalysis })}
                className={`w-12 h-6 rounded-full transition-all duration-200 ${
                  config?.visaAnalysis ? 'bg-success' : 'bg-muted'
                }`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow-md transition-all duration-200 ${
                    config?.visaAnalysis ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* API Status */}
      <div className="bg-gradient-to-br from-success/10 to-trust/10 rounded-xl p-6 border border-success/20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
          <h3 className="text-lg font-bold text-foreground">API Status: Active</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-muted-foreground">Requests Today</div>
            <div className="text-2xl font-bold text-foreground">12,847</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Avg Response</div>
            <div className="text-2xl font-bold text-foreground">1.2s</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Success Rate</div>
            <div className="text-2xl font-bold text-success">99.8%</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Tokens Used</div>
            <div className="text-2xl font-bold text-foreground">2.4M</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIConfiguration;